import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Phone, Clock, User, Target } from "lucide-react";
import { RecordingPlayer } from "@/components/recordings/RecordingPlayer";
import { CallNotes } from "@/components/calls/CallNotes";
import { OutcomeTagging } from "@/components/calls/OutcomeTagging";

export default function CallDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Call Details | Calie.ai';
  }, []);

  const { data: call } = useQuery({
    queryKey: ['call', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_calls')
        .select('*, transcript, ai_summary, sentiment_score, call_outcome, agent_notes, agent_notes_updated_at')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data;
    }
  });
  
  const adId = call?.ad_id;
  const campaignId = call?.campaign_id;

  const { data: ad } = useQuery({
    queryKey: ['ad', adId],
    enabled: !!adId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_ads')
        .select('id, name')
        .eq('id', adId)
        .maybeSingle();
      if (error) throw error;
      return data;
    }
  });

  const { data: campaign } = useQuery({
    queryKey: ['campaign', campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      const { data, error} = await supabase
        .from('calie_campaigns')
        .select('campaign_id, name')
        .eq('campaign_id', campaignId)
        .maybeSingle();
      if (error) throw error;
      return data;
    }
  });

  const { data: events } = useQuery({
    queryKey: ['call-events', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_call_events')
        .select('*')
        .eq('call_id', id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data || [];
    }
  });

  const { data: recordings } = useQuery({
    queryKey: ['call-recordings', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_recordings')
        .select('*')
        .eq('call_id', id);
      if (error) throw error;
      return data || [];
    }
  });

  const { data: smsConversations } = useQuery({
    queryKey: ['call-sms', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('calie_sms_conversations')
        .select('*')
        .eq('call_id', id)
        .order('sent_at', { ascending: true });
      if (error) throw error;
      return data || [];
    }
  });

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': case 'no-answer': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  return (
    <MainLayout>
      <main className="space-y-4 sm:space-y-6">
        <header className="space-y-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/calls')}
            className="min-h-[44px] -ml-2 mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Calls
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Call Details</h1>
          <p className="text-sm text-muted-foreground">View call information and timeline.</p>
        </header>

        <div className="grid gap-4 sm:gap-6">
          {/* Overview and Recordings */}
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <User className="h-3 w-3" /> From
                    </span>
                    <span className="font-medium truncate ml-2 max-w-[180px]">{call?.from_number || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">To</span>
                    <span className="truncate ml-2 max-w-[180px]">{call?.to_number || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Target className="h-3 w-3" /> Campaign
                    </span>
                    <span>{campaign?.name || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Ad</span>
                    <span>{ad?.name || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge className={getStatusColor(call?.status || '')}>
                      {call?.status || '-'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Clock className="h-3 w-3" /> Duration
                    </span>
                    <span>{typeof call?.duration_sec === 'number' ? `${call?.duration_sec}s` : '-'}</span>
                  </div>
                  {call?.call_outcome && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Outcome</span>
                      <span className="capitalize">{call.call_outcome}</span>
                    </div>
                  )}
                  {call?.sentiment_score !== null && call?.sentiment_score !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Sentiment</span>
                      <span className={`${call.sentiment_score > 0.3 ? 'text-green-600' : call.sentiment_score < -0.3 ? 'text-red-600' : 'text-yellow-600'}`}>
                        {call.sentiment_score > 0.3 ? '😊 Positive' : call.sentiment_score < -0.3 ? '😞 Negative' : '😐 Neutral'} 
                        ({call.sentiment_score.toFixed(2)})
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Recordings</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                {(recordings || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No recordings available.</p>
                ) : (
                  <div className="space-y-3">
                    {(recordings || []).map((r: any) => (
                      <RecordingPlayer 
                        key={r.id} 
                        recordingId={r.id} 
                        recordingUrl={r.url} 
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* AI Analysis */}
          {call?.ai_summary && typeof call.ai_summary === 'object' && (
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">AI Analysis</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="space-y-4 text-sm">
                  <div>
                    <span className="text-muted-foreground font-medium">Summary:</span>
                    <p className="mt-1">{(call.ai_summary as any)?.summary}</p>
                  </div>
                  {(call.ai_summary as any)?.customer_intent && (
                    <div>
                      <span className="text-muted-foreground font-medium">Customer Intent:</span>
                      <p className="mt-1">{(call.ai_summary as any).customer_intent}</p>
                    </div>
                  )}
                  {(call.ai_summary as any)?.insights && Array.isArray((call.ai_summary as any).insights) && (call.ai_summary as any).insights.length > 0 && (
                    <div>
                      <span className="text-muted-foreground font-medium">Key Insights:</span>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        {(call.ai_summary as any).insights.map((insight: string, index: number) => (
                          <li key={index}>{insight}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(call.ai_summary as any)?.next_steps && Array.isArray((call.ai_summary as any).next_steps) && (call.ai_summary as any).next_steps.length > 0 && (
                    <div>
                      <span className="text-muted-foreground font-medium">Next Steps:</span>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        {(call.ai_summary as any).next_steps.map((step: string, index: number) => (
                          <li key={index}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transcript */}
          {call?.transcript && (
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Transcript</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="text-sm bg-muted p-3 sm:p-4 rounded-lg border max-h-64 overflow-y-auto">
                  {call.transcript}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Agent Notes */}
          {id && (
            <CallNotes
              callId={id}
              initialNotes={call?.agent_notes}
              lastUpdated={call?.agent_notes_updated_at}
            />
          )}

          {/* Outcome Tags */}
          {id && (
            <OutcomeTagging callId={id} />
          )}

          {/* SMS Conversation */}
          {smsConversations && smsConversations.length > 0 && (
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg">SMS Conversation</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <div className="space-y-2">
                  {smsConversations.map((sms: any) => (
                    <div key={sms.id} className={`p-3 rounded-lg text-sm ${
                      sms.message_direction === 'outbound' 
                        ? 'bg-primary/10 text-foreground ml-4 sm:ml-8' 
                        : 'bg-muted text-foreground mr-4 sm:mr-8'
                    }`}>
                      <div className="font-medium text-xs mb-1">
                        {sms.message_direction === 'outbound' ? 'To Customer' : 'From Customer'}
                        <span className="text-muted-foreground ml-2">
                          {new Date(sms.sent_at).toLocaleString()}
                        </span>
                      </div>
                      <div>{sms.message_body}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Events */}
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg">Events Timeline</CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
              {(events || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No events recorded.</p>
              ) : (
                <ol className="space-y-3 text-sm">
                  {(events || []).map((e: any) => (
                    <li key={e.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString()}
                      </span>
                      <span className="hidden sm:inline">·</span>
                      <span>{e.event}</span>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </MainLayout>
  );
}
